import { forwardRef, useMemo, useState, useEffect } from 'react';
import { TextInput, TextInputProps, Platform } from 'react-native';

type MaskedTextInputProps = Omit<TextInputProps, 'onChangeText' | 'value'> & {
  mask?: string;
  value?: string | null;
  onChangeText: (formatted: string, raw?: string) => void;
};

// Um slot por posição dentro de colchetes, ou literal fora deles.
// Nomenclatura = mesma do react-native-text-input-mask:
//   0 → digit     (dígito obrigatório)
//   9 → optdigit  (dígito opcional)
//   A → alpha     (letra obrigatória)
//   a → optalpha  (letra opcional)
//   _ → anysym    (qualquer char obrigatório)
//   - → optany    (qualquer char opcional)   ← dentro de colchetes
type SlotType = 'digit' | 'optdigit' | 'alpha' | 'optalpha' | 'anysym' | 'optany';
type Slot =
  | { kind: 'slot'; type: SlotType }
  | { kind: 'lit'; text: string };

function parseMask(mask: string): { slots: Slot[]; hasAlpha: boolean } {
  const slots: Slot[] = [];
  let i = 0, hasAlpha = false;

  while (i < mask.length) {
    if (mask[i] === '[') {
      let j = i + 1;
      while (j < mask.length && mask[j] !== ']') {
        const c = mask[j];
        if      (c === '0') slots.push({ kind: 'slot', type: 'digit' });
        else if (c === '9') slots.push({ kind: 'slot', type: 'optdigit' });
        else if (c === 'A') { slots.push({ kind: 'slot', type: 'alpha' }); hasAlpha = true; }
        else if (c === 'a') { slots.push({ kind: 'slot', type: 'optalpha' }); hasAlpha = true; }
        else if (c === '_') { slots.push({ kind: 'slot', type: 'anysym' }); hasAlpha = true; }
        else if (c === '-') { slots.push({ kind: 'slot', type: 'optany' }); hasAlpha = true; }
        j++;
      }
      i = mask[j] === ']' ? j + 1 : j;
    } else {
      let j = i, lit = '';
      while (j < mask.length && mask[j] !== '[') { lit += mask[j]; j++; }
      if (lit) slots.push({ kind: 'lit', text: lit });
      i = j;
    }
  }

  return { slots, hasAlpha };
}

function charMatchesSlot(type: SlotType, c: string): boolean {
  switch (type) {
    case 'digit':
    case 'optdigit':
      return /[0-9]/.test(c);
    case 'alpha':
    case 'optalpha':
      return /[A-Za-z]/.test(c);
    case 'anysym':
    case 'optany':
      return true;
  }
}

// Aplica a máscara validando tipo por slot.
// Chars que não batem com o slot atual são descartados (pulados).
function applyMask(slots: Slot[], raw: string): string {
  if (!raw) return '';
  let out = '', rawIdx = 0;

  for (const slot of slots) {
    if (slot.kind === 'lit') {
      if (rawIdx < raw.length) out += slot.text;
      continue;
    }

    // Consome chars do raw até encontrar um que bata com o slot
    let placed = false;
    while (rawIdx < raw.length) {
      const c = raw[rawIdx];
      rawIdx++;
      if (charMatchesSlot(slot.type, c)) {
        out += c;
        placed = true;
        break;
      }
      // char não bate → descartado, tenta próximo char com o mesmo slot
    }

    if (!placed) break; // sem chars válidos restantes
  }

  return out;
}

// react-native usa nomes de keyboardType diferentes do atributo HTML inputmode.
function keyboardTypeToInputMode(keyboardType: TextInputProps['keyboardType']): string {
  switch (keyboardType) {
    case 'number-pad':
    case 'numeric':
      return 'numeric';
    case 'decimal-pad':
      return 'decimal';
    case 'phone-pad':
      return 'tel';
    case 'email-address':
      return 'email';
    case 'url':
      return 'url';
    default:
      return 'text';
  }
}

export const MaskedTextInput = forwardRef<TextInput, MaskedTextInputProps>(({
  mask = '',
  value = null,
  onChangeText,
  keyboardType = 'default',
  ...props
}, ref) => {
  const hasMask = mask.length > 0;

  const spec = useMemo(
    () => hasMask ? parseMask(mask) : { slots: [] as Slot[], hasAlpha: false },
    [hasMask, mask],
  );

  // inner sempre armazena o valor FORMATADO (com separadores e validação)
  const [inner, setInner] = useState<string>(() =>
    typeof value === 'string' && hasMask
      ? applyMask(parseMask(mask).slots, value.toUpperCase())
      : typeof value === 'string' ? value : '',
  );

  // Quando o pai atualiza value (raw sem separadores), re-aplica a máscara
  useEffect(() => {
    if (typeof value !== 'string') return;
    const formatted = hasMask
      ? applyMask(spec.slots, value.toUpperCase())
      : value;
    setInner(formatted);
  }, [value, hasMask, spec]);

  const handleChange = (input?: string) => {
    const text = String(input ?? '');

    if (!hasMask) {
      setInner(text);
      onChangeText(text, text);
      return;
    }

    // Remove separadores literais; mantém apenas alfanum.
    const cleaned = text.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    const formatted = applyMask(spec.slots, cleaned);
    // raw = chars que a máscara efetivamente aceitou (sem literais)
    const raw = formatted.replace(/[^A-Za-z0-9]/g, '');

    setInner(formatted);
    onChangeText(formatted, raw);
  };

  const resolvedKeyboardType = Platform.OS === 'web' ? 'default' : keyboardType;
  const webProps = Platform.OS === 'web'
    ? ({ inputMode: keyboardTypeToInputMode(keyboardType) } as any)
    : {};

  return (
    <TextInput
      ref={ref}
      value={inner}
      onChangeText={handleChange}
      keyboardType={resolvedKeyboardType}
      {...webProps}
      {...props}
    />
  );
});

export default MaskedTextInput;
